import { FadeIn, Section } from "./ui";
import { ArrowRight } from "lucide-react";
import { motion } from "motion/react";

const steps = [
  {
    num: "01",
    title: "Diagnóstico 360º",
    desc: "Mapeamos todo o negócio antes de qualquer proposta",
  },
  {
    num: "02",
    title: "Branding Real",
    desc: "Identidade que reflete quem você é — não o que é genérico",
  },
  {
    num: "03",
    title: "Estratégia",
    desc: "Planejamento com metas, canais e prioridades claras",
  },
  {
    num: "04",
    title: "Automação & IA",
    desc: "Tecnologia que escala sem aumentar o time",
  },
  {
    num: "05",
    title: "Gestão de Resultado",
    desc: "Relatório que você entende — e que muda decisões",
  },
];

export function PrismaMethod() {
  return (
    <Section id="metodo-prisma" className="py-24 md:py-32 relative overflow-clip bg-brand-dark">
      {/* Prism Background Effects */}
      <div className="absolute inset-0 pointer-events-none z-0">
        {/* Animated Light Beams (Prism Effect) */}
        <motion.div 
          animate={{ 
            rotate: [0, 5, -5, 0],
            scale: [1, 1.05, 0.95, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-0 left-1/4 w-[1px] h-[150vh] bg-gradient-to-b from-transparent via-white to-transparent blur-[2px] origin-top -rotate-45"
        />
        
        {/* Refracted Colors */}
        <motion.div 
          animate={{ 
            x: [0, 30, -30, 0],
            y: [0, -20, 20, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/3 w-[600px] h-[600px] bg-gradient-to-tr from-purple-500/20 via-brand-accent/20 to-blue-500/20 rounded-full blur-[120px] mix-blend-screen"
        />
        <motion.div 
          animate={{ 
            x: [0, -40, 40, 0],
            y: [0, 30, -30, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-gradient-to-bl from-emerald-500/10 via-brand-accent/10 to-rose-500/10 rounded-full blur-[100px] mix-blend-screen"
        />
      </div>
      
      {/* Huge Vertical Watermark */}
      <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 rotate-90 origin-center text-[25vw] font-black tracking-tighter text-brand-light/[0.02] pointer-events-none z-0 select-none mix-blend-overlay">
        PRISMA
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start relative">
          
          {/* Left Column - Fixed/Sticky */}
          <div className="flex flex-col items-start relative lg:sticky lg:top-32 lg:h-[calc(100vh-16rem)] lg:justify-center">
            <FadeIn>
              <h2 className="text-6xl md:text-8xl lg:text-9xl font-black uppercase tracking-tighter leading-[0.85] mb-10 relative">
                Método <br />
                <span className="text-brand-accent relative inline-block">
                  Prisma
                  {/* Subtle glow behind the word Prisma */}
                  <span className="absolute inset-0 bg-brand-accent/40 blur-3xl -z-10"></span>
                </span>
              </h2>
            </FadeIn>
            
            <FadeIn delay={0.1}>
              <div className="border-l-4 border-brand-accent pl-6 md:pl-8 mb-12 relative">
                {/* Glassmorphism backing for quote */}
                <div className="absolute inset-0 bg-gradient-to-r from-brand-light/5 to-transparent -z-10 rounded-r-2xl backdrop-blur-sm"></div>
                <p className="text-xl md:text-2xl text-brand-light/80 font-medium leading-relaxed py-4">
                  "A maioria das agências pega o briefing e entrega o pacote que já tinha pronto. A P2A faz diferente: começa pelo diagnóstico real do negócio, não pelo que já sabe entregar."
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <a 
                href="https://wa.me/5524993144981?text=Vim%20do%20site%20e%20quero%20aplicar%20o%20M%C3%A9todo%20Prisma%20no%20meu%20neg%C3%B3cio" 
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-brand-light bg-brand-accent rounded-full hover:bg-brand-light hover:text-brand-dark hover:scale-105 transition duration-500 overflow-hidden shadow-[0_0_30px_rgba(242,125,38,0.3)] hover:shadow-[0_0_50px_rgba(255,255,255,0.5)] w-full sm:w-auto"
              >
                <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></span>
                <span className="relative z-10 flex items-center gap-3">
                  Quero aplicar o Método no meu negócio
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
            </FadeIn>
          </div>

          {/* Right Column - Scrolling Cards */}
          <div className="flex flex-col relative z-10 lg:pb-32">
            <FadeIn delay={0.2} className="mb-10 lg:mt-16">
              <div className="flex items-center gap-4 mb-4">
                <div className="h-[2px] w-8 bg-brand-accent"></div>
                <span className="text-brand-accent font-bold uppercase tracking-widest text-sm">Por que funciona</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-black uppercase tracking-tighter leading-[0.9]">
                Marketing que olha <br className="hidden md:block" />
                o negócio <span className="font-highlight italic text-brand-accent lowercase font-normal">inteiro</span>
              </h3>
            </FadeIn>

            <div className="flex flex-col gap-6 relative">
              {steps.map((step, index) => (
                <div 
                  key={index} 
                  className="sticky"
                  style={{ 
                    top: `calc(120px + ${index * 24}px)`,
                    zIndex: 10 + index 
                  }}
                >
                  <FadeIn delay={0.1}>
                    <div className="group flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 p-6 md:p-8 rounded-3xl bg-brand-dark border border-brand-light/20 hover:border-brand-accent/50 transition duration-300 shadow-[0_-10px_30px_rgba(0,0,0,0.5)] relative overflow-hidden">
                      {/* Hover gradient effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-brand-accent/0 via-brand-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                      
                      <div className="flex items-center gap-6 sm:w-1/3 shrink-0 relative z-10">
                        <span className="text-xl font-black text-brand-light/40 group-hover:text-brand-accent transition-colors">
                          {step.num}
                        </span>
                        <h4 className="text-xl font-bold font-display text-brand-light">
                          {step.title}
                        </h4>
                      </div>
                      <p className="text-brand-light/70 text-base md:text-lg leading-relaxed sm:w-2/3 relative z-10">
                        {step.desc}
                      </p>
                    </div>
                  </FadeIn>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </Section>
  );
}
